/**
 * DevTools Page - Development utilities
 * 
 * WARNING: These tools can permanently delete data!
 * Use only for development and testing.
 */

import React, { useState, useEffect } from'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from'../components/ui/Card'
import { Button } from'../components/ui/Button'
import { AlertTriangle, Trash2, RefreshCw, Database, AlertCircle, Calendar, CloudOff } from'lucide-react'

function formatDeletedCountFragment(value: unknown): string {
 if (value == null) return '0'
 if (typeof value === 'number' || typeof value === 'boolean') return String(value)
 if (typeof value === 'string') return value
 if (typeof value === 'object') {
 try {
 return JSON.stringify(value)
 } catch {
 return '[object]'
 }
 }
 return String(value)
}

interface ItemCounts {
 dreams: number
 aspirations: number
 goals: number
 tasks: number
 action_items: number
 roadblocks: number
 knowledge_skills: number
}

interface HierarchyDebugItem {
 id: number
 title: string
 parent_dream_id?: number
 parent_aspiration_id?: number
 parent_goal_id?: number
 parent_task_id?: number
 goal_id?: number
}

interface HierarchyDebugData {
 dreams: HierarchyDebugItem[]
 aspirations: HierarchyDebugItem[]
 goals: HierarchyDebugItem[]
 tasks: HierarchyDebugItem[]
 action_items: HierarchyDebugItem[]
 roadblocks: HierarchyDebugItem[]
 knowledge_skills: HierarchyDebugItem[]
}

interface ScheduleCounts {
 total: number
 google_synced: number
 pending_sync: number
 local_only: number
 pending_deletions: number
}

export const DevTools: React.FC = () => {
 const [counts, setCounts] = useState<ItemCounts | null>(null)
 const [hierarchyData, setHierarchyData] = useState<HierarchyDebugData | null>(null)
 const [loading, setLoading] = useState(false)
 const [error, setError] = useState<string | null>(null)
 const [showConfirmation, setShowConfirmation] = useState(false)
 const [clearLoading, setClearLoading] = useState(false)
 const [clearResult, setClearResult] = useState<any>(null)
 const [clearChats, setClearChats] = useState(false)
 const [clearPlaces, setClearPlaces] = useState(false)
 
 // Schedule items state
 const [scheduleCounts, setScheduleCounts] = useState<ScheduleCounts | null>(null)
 const [showScheduleConfirmation, setShowScheduleConfirmation] = useState(false)
 const [clearScheduleLoading, setClearScheduleLoading] = useState(false)
 const [clearScheduleResult, setClearScheduleResult] = useState<any>(null)

 // Load counts on mount
 useEffect(() => {
 loadCounts()
 loadScheduleCounts()
 }, [])

 const loadCounts = async () => {
 try {
 setLoading(true)
 setError(null)
 
 const response = await fetch('/api/dev/count-items', {
 credentials:'include'
 })
 
 if (!response.ok) {
 throw new Error('Failed to load item counts')
 }
 
 const data = await response.json()
 setCounts(data.counts)
 } catch (err) {
 console.error('Error loading counts:', err)
 setError(err instanceof Error ? err.message :'Failed to load counts')
 } finally {
 setLoading(false)
 }
 }

 const loadHierarchyDebug = async () => {
 try {
 setLoading(true)
 setError(null)
 
 const response = await fetch('/api/dev/list-items-with-hierarchy', {
 credentials:'include'
 })
 
 if (!response.ok) {
 throw new Error('Failed to load hierarchy data')
 }
 
 const data = await response.json()
 setHierarchyData(data.items)
 console.log('📊 Hierarchy Debug Data:', data.items)
 } catch (err) {
 console.error('Error loading hierarchy:', err)
 setError(err instanceof Error ? err.message :'Failed to load hierarchy')
 } finally {
 setLoading(false)
 }
 }

 const handleClearAll = async () => {
 try {
 setClearLoading(true)
 setClearResult(null)
 setError(null)
 
 const response = await fetch('/api/dev/clear-all-items', {
 method:'POST',
 credentials:'include',
 headers: {
'Content-Type':'application/json'
 },
 body: JSON.stringify({ include_chats: clearChats, include_places: clearPlaces })
 })
 
 if (!response.ok) {
 throw new Error('Failed to clear items')
 }
 
 const result = await response.json()
 setClearResult(result)
 setShowConfirmation(false)
 
 // Reload counts
 await loadCounts()
 await loadScheduleCounts()
 setHierarchyData(null)
 } catch (err) {
 console.error('Error clearing items:', err)
 setError(err instanceof Error ? err.message :'Failed to clear items')
 } finally {
 setClearLoading(false)
 }
 }

 const loadScheduleCounts = async () => {
 try {
 const response = await fetch('/api/dev/count-schedule-items', {
 credentials:'include'
 })
 
 if (!response.ok) {
 throw new Error('Failed to load schedule counts')
 }
 
 const data = await response.json()
 setScheduleCounts(data.counts)
 } catch (err) {
 console.error('Error loading schedule counts:', err)
 }
 }

 const handleClearSchedule = async () => {
 try {
 setClearScheduleLoading(true)
 setClearScheduleResult(null)
 setError(null)
 
 const response = await fetch('/api/dev/clear-schedule-items', {
 method:'POST',
 credentials:'include',
 headers: {
'Content-Type':'application/json'
 }
 })
 
 if (!response.ok) {
 throw new Error('Failed to clear schedule items')
 }
 
 const result = await response.json()
 setClearScheduleResult(result)
 setShowScheduleConfirmation(false)
 
 // Reload schedule counts
 await loadScheduleCounts()
 } catch (err) {
 console.error('Error clearing schedule items:', err)
 setError(err instanceof Error ? err.message :'Failed to clear schedule items')
 } finally {
 setClearScheduleLoading(false)
 }
 }

 const totalItems = counts ? Object.values(counts).reduce((sum, count) => sum + count, 0) : 0

 return (
 <div className="space-y-6 p-6">
 {/* Warning Banner */}
 <Card className="border-bad bg-bad/10">
 <CardContent className="pt-6">
 <div className="flex items-start gap-3">
 <AlertTriangle className="h-6 w-6 text-bad flex-shrink-0 mt-1" />
 <div>
 <h3 className="font-semibold text-bad mb-1">Development Tools</h3>
 <p className="text-sm text-bad">
 These tools can permanently delete data. Use with caution and only in development environments.
 </p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Item Counts */}
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <Database className="h-5 w-5 text-brand" />
 Your Hierarchy Items
 </CardTitle>
 <CardDescription>
 Current count of all hierarchy items in your account
 </CardDescription>
 </CardHeader>
 <CardContent>
 {loading && !counts && (
 <div className="flex items-center justify-center py-8">
 <RefreshCw className="h-6 w-6 animate-spin text-brand mr-2" />
 <span className="text-secondary">Loading...</span>
 </div>
 )}

 {error && (
 <div className="flex items-center gap-2 text-bad mb-4">
 <AlertCircle className="h-5 w-5" />
 <span>{error}</span>
 </div>
 )}

 {counts && (
 <div className="space-y-4">
 <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
 <div className="text-center p-4 bg-brand-subtle rounded-lg">
 <div className="text-2xl font-semibold text-brand">{counts.dreams}</div>
 <div className="text-sm text-secondary mt-1">Dreams</div>
 </div>
 <div className="text-center p-4 bg-brand-subtle rounded-lg">
 <div className="text-2xl font-semibold text-brand">{counts.aspirations}</div>
 <div className="text-sm text-secondary mt-1">Aspirations</div>
 </div>
 <div className="text-center p-4 bg-ok/10 rounded-lg">
 <div className="text-2xl font-semibold text-ok">{counts.goals}</div>
 <div className="text-sm text-secondary mt-1">Goals</div>
 </div>
 <div className="text-center p-4 bg-brand-subtle rounded-lg">
 <div className="text-2xl font-semibold text-brand">{counts.tasks}</div>
 <div className="text-sm text-secondary mt-1">Tasks</div>
 </div>
 <div className="text-center p-4 bg-brand-subtle rounded-lg">
 <div className="text-2xl font-semibold text-brand">{counts.action_items}</div>
 <div className="text-sm text-secondary mt-1">Action Items</div>
 </div>
 <div className="text-center p-4 bg-warn/10 rounded-lg">
 <div className="text-2xl font-semibold text-warn">{counts.roadblocks}</div>
 <div className="text-sm text-secondary mt-1">Roadblocks</div>
 </div>
 <div className="text-center p-4 bg-brand-subtle rounded-lg">
 <div className="text-2xl font-semibold text-brand">{counts.knowledge_skills}</div>
 <div className="text-sm text-secondary mt-1">Knowledge/Skills</div>
 </div>
 <div className="text-center p-4 bg-surface-alt rounded-lg border-2 border-soft">
 <div className="text-2xl font-semibold text-primary">{totalItems}</div>
 <div className="text-sm text-secondary mt-1 font-semibold">Total</div>
 </div>
 </div>

 <div className="flex gap-2">
 <Button onClick={loadCounts} variant="outline" disabled={loading}>
 <RefreshCw className={`h-4 w-4 mr-2 ${loading ?'animate-spin' :''}`} />
 Refresh Counts
 </Button>
 <Button onClick={loadHierarchyDebug} variant="outline" disabled={loading}>
 <Database className="h-4 w-4 mr-2" />
 Debug Hierarchy
 </Button>
 </div>
 </div>
 )}
 </CardContent>
 </Card>

 {/* Hierarchy Debug */}
 {hierarchyData && (
 <Card>
 <CardHeader>
 <CardTitle>Hierarchy Debug View</CardTitle>
 <CardDescription>
 Shows parent relationships for all items (useful for debugging)
 </CardDescription>
 </CardHeader>
 <CardContent>
 <div className="space-y-4 max-h-96 overflow-y-auto">
 {Object.entries(hierarchyData).map(([type, items]) => (
 items.length > 0 && (
 <div key={type} className="border-l-4 border-brand pl-4">
 <h4 className="font-semibold text-lg mb-2 capitalize">{type.replace('_','')}</h4>
 <div className="space-y-1 text-sm">
 {items.map((item: HierarchyDebugItem) => (
 <div key={item.id} className="flex items-start gap-2">
 <span className="font-mono text-secondary">#{item.id}</span>
 <span className="flex-1">{item.title}</span>
 <span className="text-secondary">
 {item.parent_dream_id &&`Dream: ${item.parent_dream_id}`}
 {item.parent_aspiration_id &&`Aspiration: ${item.parent_aspiration_id}`}
 {item.parent_goal_id &&`Goal: ${item.parent_goal_id}`}
 {item.parent_task_id &&`Task: ${item.parent_task_id}`}
 {item.goal_id &&`Goal: ${item.goal_id}`}
 {!item.parent_dream_id && !item.parent_aspiration_id && !item.parent_goal_id && !item.parent_task_id && !item.goal_id &&'(orphan)'}
 </span>
 </div>
 ))}
 </div>
 </div>
 )
 ))}
 </div>
 </CardContent>
 </Card>
 )}

 {/* Clear Result */}
 {clearResult && (
 <Card className="border-ok bg-ok/10">
 <CardContent className="pt-6">
 <h4 className="font-semibold text-ok mb-2">
 ✅ Successfully Cleared {clearResult.total_deleted} Items
 </h4>
 <div className="text-sm text-ok break-words">
 {Object.entries(clearResult.deleted_counts).map(([type, count]) => (
 <div key={type}>
 {type.replace(/_/g, ' ')}: {formatDeletedCountFragment(count)}
 </div>
 ))}
 </div>
 </CardContent>
 </Card>
 )}

 {/* Clear Schedule Items */}
 <Card className="border-warn">
 <CardHeader>
 <CardTitle className="flex items-center gap-2 text-warn">
 <Calendar className="h-5 w-5" />
 Clear Schedule Items
 </CardTitle>
 <CardDescription>
 Clear all schedule entries and queue Google Calendar deletions
 </CardDescription>
 </CardHeader>
 <CardContent>
 {scheduleCounts && (
 <div className="mb-4 grid grid-cols-2 md:grid-cols-5 gap-3">
 <div className="text-center p-3 bg-surface rounded-lg">
 <div className="text-xl font-semibold text-primary">{scheduleCounts.total}</div>
 <div className="text-xs text-secondary">Total</div>
 </div>
 <div className="text-center p-3 bg-ok/10 rounded-lg">
 <div className="text-xl font-semibold text-ok">{scheduleCounts.google_synced}</div>
 <div className="text-xs text-secondary">Google Synced</div>
 </div>
 <div className="text-center p-3 bg-brand-subtle rounded-lg">
 <div className="text-xl font-semibold text-brand">{scheduleCounts.pending_sync}</div>
 <div className="text-xs text-secondary">Pending Sync</div>
 </div>
 <div className="text-center p-3 bg-surface rounded-lg">
 <div className="text-xl font-semibold text-secondary">{scheduleCounts.local_only}</div>
 <div className="text-xs text-secondary">Local Only</div>
 </div>
 <div className="text-center p-3 bg-bad/10 rounded-lg">
 <div className="text-xl font-semibold text-bad">{scheduleCounts.pending_deletions}</div>
 <div className="text-xs text-secondary">Pending Delete</div>
 </div>
 </div>
 )}

 {clearScheduleResult && (
 <div className="mb-4 p-4 bg-ok/10 border border-ok rounded-lg">
 <h4 className="font-semibold text-ok mb-1">
 ✅ {clearScheduleResult.message}
 </h4>
 {clearScheduleResult.google_deletions_queued > 0 && (
 <p className="text-sm text-ok">
 <CloudOff className="inline h-4 w-4 mr-1" />
 {clearScheduleResult.google_deletions_queued} events queued for Google Calendar deletion.
 Use"Push to Google" on the Calendar page to remove them.
 </p>
 )}
 </div>
 )}

 <div className="flex gap-2">
 <Button onClick={loadScheduleCounts} variant="outline" disabled={loading}>
 <RefreshCw className={`h-4 w-4 mr-2 ${loading ?'animate-spin' :''}`} />
 Refresh
 </Button>
 
 {!showScheduleConfirmation ? (
 <Button
 variant="destructive"
 onClick={() => setShowScheduleConfirmation(true)}
 disabled={!scheduleCounts || scheduleCounts.total === 0 || loading}
 >
 <Trash2 className="h-4 w-4 mr-2" />
 Clear All Schedule Items ({scheduleCounts?.total || 0})
 </Button>
 ) : (
 <div className="flex-1">
 <div className="p-3 mb-3 bg-warn/10 border border-warn rounded-lg">
 <p className="font-semibold text-warn mb-1">
 ⚠️ Are you sure?
 </p>
 <p className="text-sm text-warn">
 This will delete <strong>{scheduleCounts?.total || 0} schedule items</strong>.
 {scheduleCounts && scheduleCounts.google_synced > 0 && (
 <> <strong>{scheduleCounts.google_synced}</strong> events synced to Google will be queued for deletion.</>
 )}
 </p>
 </div>
 <div className="flex gap-2">
 <Button
 variant="destructive"
 onClick={handleClearSchedule}
 disabled={clearScheduleLoading}
 >
 {clearScheduleLoading ? (
 <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Deleting...</>
 ) : (
 <><Trash2 className="h-4 w-4 mr-2" />Yes, Clear Schedule</>
 )}
 </Button>
 <Button
 variant="outline"
 onClick={() => setShowScheduleConfirmation(false)}
 disabled={clearScheduleLoading}
 >
 Cancel
 </Button>
 </div>
 </div>
 )}
 </div>
 </CardContent>
 </Card>

 {/* Clear All Items */}
 <Card className="border-bad/30">
 <CardHeader>
 <CardTitle className="flex items-center gap-2 text-bad">
 <Trash2 className="h-5 w-5" />
 Clear All Items
 </CardTitle>
 <CardDescription>
 Permanently delete all hierarchy items from your account
 </CardDescription>
 </CardHeader>
 <CardContent>
 {!showConfirmation ? (
 <Button
 variant="destructive"
 onClick={() => setShowConfirmation(true)}
 disabled={totalItems === 0 || loading}
 >
 <Trash2 className="h-4 w-4 mr-2" />
 Clear All ({totalItems} items)
 </Button>
 ) : (
 <div className="space-y-4">
 <div className="p-4 bg-warn/10 border border-warn rounded-lg">
 <p className="font-semibold text-warn mb-2">
 ⚠️ Are you absolutely sure?
 </p>
 <p className="text-sm text-warn">
 This will permanently delete <strong>{totalItems} items</strong> from your account.
 This action cannot be undone.
 </p>
 </div>
 <div className="space-y-2 text-sm p-3 border border-brand-subtle rounded-lg">
 <p className="text-secondary">
 Chat history and My World (people &amp; places) are <strong>kept</strong> by default so you can compare old vs new suggestions. Also clear:
 </p>
 <label className="flex items-center gap-2 cursor-pointer">
 <input type="checkbox" checked={clearChats} onChange={(e) => setClearChats(e.target.checked)} disabled={clearLoading} />
 Chat history
 </label>
 <label className="flex items-center gap-2 cursor-pointer">
 <input type="checkbox" checked={clearPlaces} onChange={(e) => setClearPlaces(e.target.checked)} disabled={clearLoading} />
 Places
 </label>
 </div>
 <div className="flex gap-2">
 <Button
 variant="destructive"
 onClick={handleClearAll}
 disabled={clearLoading}
 >
 {clearLoading ? (
 <>
 <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
 Deleting...
 </>
 ) : (
 <>
 <Trash2 className="h-4 w-4 mr-2" />
 Yes, Delete Everything
 </>
 )}
 </Button>
 <Button
 variant="outline"
 onClick={() => setShowConfirmation(false)}
 disabled={clearLoading}
 >
 Cancel
 </Button>
 </div>
 </div>
 )}
 </CardContent>
 </Card>
 </div>
 )
}
